import {Alpha3CountryCode} from 'platform/components';

import {always, isNotNil} from 'ramda';

import {omneticApi} from '@dms/api/base';
import {
  BlockUserApiArg,
  BlockUserApiResponse,
  ChangeCurrentUserPasswordApiArg,
  ChangeCurrentUserPasswordApiResponse,
  CreateExternalUserApiArg,
  CreateExternalUserApiResponse,
  CreateUserApiArg,
  CreateUserApiResponse,
  GetCurrentUserInfoApiResponse,
  GetOtherUserSettingsApiArg,
  GetOtherUserSettingsApiResponse,
  GetUserApiArg,
  GetUserApiResponse,
  GetUserAuctionStatsApiResponse,
  GetUserDashboardSettingsApiArg,
  GetUserDashboardSettingsApiResponse,
  GetUserMenuPinsArg,
  GetUserMenuPinsResponse,
  GetUsersApiResponse,
  GetUserSettingsApiResponse,
  GetUserStats,
  GetUserStatsApiResponse,
  InactivityPeriodType,
  MenuItemPin,
  PatchCurrentUserApiArg,
  PatchCurrentUserApiResponse,
  PatchUserApiArg,
  PatchUserApiResponse,
  SetUserMenuPinsArg,
  PutUserDashboardSettingsApiArg,
  PutUserDashboardSettingsApiResponse,
  SetOtherUserSettingsItemApiArg,
  SetOtherUserSettingsItemApiResponse,
  SetUserSettingsItemApiArg,
  SetUserSettingsItemApiResponse,
  UnblockUserApiArg,
  UnblockUserApiResponse,
  SetUserMenuPinsResponse,
  GetUserAvatarApiResponse,
  GetUserAvatarApiArg,
  TRANSLATED_LANGUAGES,
  COMMON_CURRENCY,
  UserSidebarIconSize,
  ActivateUserApiArg,
  ActivateUserApiResponse,
  DeactivateUserApiArg,
  DeactivateUserApiResponse,
  SetUserAwayApiArg,
  SetUserAwayApiResponse,
  ActivateUserApiResponseSchema,
  ActivateUserApiArgSchema,
  DeactivateUserApiResponseSchema,
  DeactivateUserApiArgSchema,
  SetUserAwayApiResponseSchema,
  SetUserAwayApiArgSchema,
  ReActivateUserApiArg,
  ReActivateUserApiResponse,
  ReActivateUserRequestBodySchema,
  ReActivateUserResponseSchema,
  ReinviteUserApiArg,
  ReinviteUserApiResponse,
  InitializeUserApiArg,
  InitializeUserApiResponse,
} from '@dms/api/shared';

import {buildArray} from 'shared/web';

const ONE_DAY_IN_SECONDS = 24 * 60 * 60;

export const userApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    getCurrentUserInfo: build.query<GetCurrentUserInfoApiResponse, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const [userInfoResponse, userSettingsResponse] = await Promise.all([
          fetchWithBaseQuery({url: `/dms/v1/user/info`}),
          fetchWithBaseQuery({url: `/dms/v1/user/settings`}),
        ]);

        if (userInfoResponse.error) {
          throw userInfoResponse.error;
        }

        if (userSettingsResponse.error) {
          throw userSettingsResponse.error;
        }

        const userInfo = Object.assign(
          {isAway: false, awayChangedAt: null},
          userInfoResponse.data as Omit<GetCurrentUserInfoApiResponse, 'settings'>
        );
        const userSettings = userSettingsResponse.data as GetUserSettingsApiResponse;

        const data: GetCurrentUserInfoApiResponse = Object.assign({}, userInfo, {
          settings: {
            currency: (userSettings?.sourcingCurrency?.value ??
              COMMON_CURRENCY.czk) as COMMON_CURRENCY,
            insurerNumber: userSettings?.insurerNumber?.value as string | undefined,
            customId: userSettings?.customId?.value as string | undefined,
            branch: (userSettings?.activeBranch?.value ?? '') as string,
            country: (userSettings?.sourcingBuyerCountry?.value ?? '') as Alpha3CountryCode,
            language: (userSettings?.selectedLanguage?.value ??
              TRANSLATED_LANGUAGES.english) as TRANSLATED_LANGUAGES,
            inactivityPeriod: (userSettings?.inactivityPeriod?.value ?? '') as InactivityPeriodType,
            sidebarIconSize: (userSettings?.sidebarIconSize?.value ??
              'default') as UserSidebarIconSize,
          },
        });

        return {data};
      },
      providesTags: ['CurrentUser'],
    }),
    getUserStats: build.query<GetUserStats, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const [userAuctionStatsResponse, userStatsResponse] = await Promise.all([
          fetchWithBaseQuery({url: `/dms/v1/user/user-auction-stats`}),
          fetchWithBaseQuery({url: `/dms/v1/user/user-stats`}),
        ]);

        if (userAuctionStatsResponse.error) {
          throw userAuctionStatsResponse.error;
        }
        if (userStatsResponse.error) {
          throw userStatsResponse.error;
        }

        const userAuctionStats = userAuctionStatsResponse.data as GetUserAuctionStatsApiResponse;
        const userStats = userStatsResponse.data as GetUserStatsApiResponse;

        const data = Object.assign({}, {userAuctionStats, userStats});

        return {data};
      },
      providesTags: ['User'],
    }),
    createExternalUser: build.mutation<CreateExternalUserApiResponse, CreateExternalUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/external`,
        method: 'POST',
        body: queryArg.createExternalUserRequestBody,
      }),
    }),
    setUserSettingsItem: build.mutation<SetUserSettingsItemApiResponse, SetUserSettingsItemApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/settings/${queryArg.settingKey}`,
        method: 'PUT',
        body: queryArg.userSettingRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) =>
        buildArray<'CurrentUser' | 'SourcingPriceMap'>(['CurrentUser']).when(
          queryArg.settingKey === 'sourcing_buyer_country',
          'SourcingPriceMap'
        ),
    }),
    blockUser: build.mutation<BlockUserApiResponse, BlockUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/block`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'Users'],
    }),
    changeCurrentUserPassword: build.mutation<
      ChangeCurrentUserPasswordApiResponse,
      ChangeCurrentUserPasswordApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/user/me/password`,
        method: 'PATCH',
        body: queryArg.changeCurrentUserPasswordRequestBody,
      }),
    }),
    createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user`,
        method: 'POST',
        body: queryArg.createUserRequestBody,
      }),
    }),
    getUsers: build.query<GetUsersApiResponse, void>({
      query: () => ({
        url: `/dms/v1/user`,
      }),
      providesTags: ['Users'],
    }),
    getUser: build.query<GetUserApiResponse, GetUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.id}`,
      }),
      providesTags: (result, error, {id}) => [{type: 'User', id}],
    }),
    patchCurrentUser: build.mutation<PatchCurrentUserApiResponse, PatchCurrentUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/me`,
        method: 'PATCH',
        body: queryArg.updateCurrentUserRequestBody,
      }),
      invalidatesTags: ['CurrentUser'],
    }),
    patchUser: build.mutation<PatchUserApiResponse, PatchUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}`,
        method: 'PATCH',
        body: queryArg.updateUserRequestBody,
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'CurrentUser'],
    }),
    initializeUser: build.mutation<InitializeUserApiResponse, InitializeUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/initialize`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'Users'],
    }),
    getOtherUserSettings: build.query<GetOtherUserSettingsApiResponse, GetOtherUserSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/settings`,
      }),
      providesTags: (result, error, {userId}) => [{type: 'OtherUserSettings', id: userId}],
    }),
    setOtherUserSettingsItem: build.mutation<
      SetOtherUserSettingsItemApiResponse,
      SetOtherUserSettingsItemApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/settings/${queryArg.settingKey}`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, {userId}) => [
        'CurrentUser',
        {type: 'OtherUserSettings', id: userId},
      ],
    }),
    unblockUser: build.mutation<UnblockUserApiResponse, UnblockUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/unblock`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'Users'],
    }),
    getUserMenuPins: build.query<GetUserMenuPinsResponse, GetUserMenuPinsArg>({
      query: () => ({
        url: `/dms/v1/web-app-user/pins`,
      }),
      providesTags: ['UserMenuPins', 'User'],
      transformResponse: (response: MenuItemPin[] | undefined) =>
        response ? response.map((i) => i?.item).filter(isNotNil) : [],
    }),
    pinUserMenuItem: build.mutation<Array<MenuItemPin>, Required<MenuItemPin>>({
      query: (queryArg) => ({
        url: `/dms/v1/web-app-user/pin`,
        method: 'PUT',
        body: queryArg,
      }),
      async onQueryStarted({item}, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          userApi.util.updateQueryData('getUserMenuPins', undefined, (draft) => [...draft, item])
        );
        await queryFulfilled.catch(patchResult.undo);
      },
    }),
    unpinUserMenuItem: build.mutation<Array<MenuItemPin>, Required<MenuItemPin>>({
      query: (queryArg) => ({
        url: `/dms/v1/web-app-user/unpin`,
        method: 'PUT',
        body: queryArg,
      }),
      async onQueryStarted({item}, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          userApi.util.updateQueryData('getUserMenuPins', undefined, (draft) =>
            draft.filter((pinnedItem) => pinnedItem !== item)
          )
        );
        await queryFulfilled.catch(patchResult.undo);
      },
    }),
    setUserMenuPins: build.mutation<SetUserMenuPinsResponse, SetUserMenuPinsArg>({
      query: (queryArg) => ({
        url: `/dms/v1/web-app-user/pins`,
        method: 'PUT',
        body: queryArg,
      }),
      async onQueryStarted({items: pins}, {dispatch, queryFulfilled}) {
        const newOrderedPins = pins.map(({item: pinId}) => pinId).filter(isNotNil);

        const patchResult = dispatch(
          userApi.util.updateQueryData('getUserMenuPins', undefined, always(newOrderedPins))
        );
        await queryFulfilled.catch(patchResult.undo);
      },
    }),
    activateUser: build.mutation<ActivateUserApiResponse, ActivateUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/activate`,
        method: 'PUT',
      }),
      extraOptions: {
        requestSchema: ActivateUserApiArgSchema,
        responseSchema: ActivateUserApiResponseSchema,
      },
      invalidatesTags: ['Users'],
    }),
    deactivateUser: build.mutation<DeactivateUserApiResponse, DeactivateUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/deactivate`,
        method: 'PUT',
      }),
      extraOptions: {
        requestSchema: DeactivateUserApiArgSchema,
        responseSchema: DeactivateUserApiResponseSchema,
      },
      invalidatesTags: ['Users'],
    }),
    setUserAway: build.mutation<SetUserAwayApiResponse, SetUserAwayApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/away`,
        method: 'PUT',
        body: {
          isAway: queryArg.isAway,
        },
      }),
      extraOptions: {
        requestSchema: SetUserAwayApiArgSchema,
        responseSchema: SetUserAwayApiResponseSchema,
      },
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'User', id: queryArg.userId},
        'CurrentUser',
      ],
    }),
    reActivateUser: build.mutation<ReActivateUserApiResponse, ReActivateUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/re-activate`,
        method: 'POST',
        body: queryArg.reActivateUserRequestBody,
      }),
      extraOptions: {
        requestSchema: ReActivateUserRequestBodySchema,
        responseSchema: ReActivateUserResponseSchema,
      },
      invalidatesTags: ['Users'],
    }),
    getUserDashboardSettings: build.query<
      GetUserDashboardSettingsApiResponse,
      GetUserDashboardSettingsApiArg
    >({
      query: () => ({
        url: `/dms/v1/user/dashboard/settings`,
      }),
      providesTags: ['DashboardSettings'],
    }),
    putUserDashboardSettings: build.mutation<
      PutUserDashboardSettingsApiResponse,
      PutUserDashboardSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/user/dashboard/settings`,
        method: 'PUT',
        body: queryArg,
      }),
      invalidatesTags: ['DashboardSettings'],
    }),
    getUserAvatarUrl: build.query<GetUserAvatarApiResponse, GetUserAvatarApiArg>({
      query: ({userId, height, width}) => ({
        url: `/dms/v1/user/${userId}/avatar`,
        params: {height, width},
        responseHandler: async (response) => {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
      }),
      keepUnusedDataFor: ONE_DAY_IN_SECONDS,
      providesTags: ['EmployeePhoto'],
    }),
    reinviteUser: build.mutation<ReinviteUserApiResponse, ReinviteUserApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/user/${queryArg.userId}/invitation/refresh`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, {userId}) => [{type: 'User', id: userId}, 'Users'],
    }),
  }),
});

export const selectCurrentUserInfo = userApi.endpoints.getCurrentUserInfo.select();

export const {
  useReinviteUserMutation,
  useGetUserAvatarUrlQuery,
  useLazyGetUserAvatarUrlQuery,
  useGetCurrentUserInfoQuery,
  useLazyGetCurrentUserInfoQuery,
  useGetUserStatsQuery,
  useCreateExternalUserMutation,
  useSetUserSettingsItemMutation,
  useBlockUserMutation,
  useChangeCurrentUserPasswordMutation,
  useCreateUserMutation,
  useGetUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  usePatchCurrentUserMutation,
  usePatchUserMutation,
  useUnblockUserMutation,
  useGetUserMenuPinsQuery,
  useSetUserMenuPinsMutation,
  usePinUserMenuItemMutation,
  useUnpinUserMenuItemMutation,
  useGetOtherUserSettingsQuery,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useSetUserAwayMutation,
  useSetOtherUserSettingsItemMutation,
  useGetUserDashboardSettingsQuery,
  useReActivateUserMutation,
  usePutUserDashboardSettingsMutation,
  useInitializeUserMutation,
} = userApi;
